import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useActionBarHeight } from "@/hooks/use-action-bar-height";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth.store";
import { GetInvoice, SaveContact } from "@/services/purchase.service";
import { toast } from "sonner";
import type { DialCode } from "@/types/dialcode";
import { GetDialCode } from "@/services/dialcode.service";
import PhoneField from "@/components/common/PhoneField";
import {
  contactSchema,
  type ContactValues,
} from "@/features/purchase-ticket/schema/contact.schema";
import { Loader2 } from "lucide-react";
import { useContactStore } from "@/stores/contact.store";
import { useTdaStore } from "@/stores/tda.store";
import { cn } from "@/lib/utils";

const FIELD_CLASS =
  "h-14 rounded-2xl border-border bg-card px-4 font-medium text-sm text-muted-foreground placeholder:text-border";

export default function ContactInfoPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const verifiedTdaPassenger = useTdaStore(
    (state) => state.verifiedTdaPassengers[0],
  );

  const paymentId = location.state?.paymentId as string | undefined;

  const [values, setValues] = useState<ContactValues>(() => ({
    firstName: verifiedTdaPassenger?.firstName ?? "",
    lastName: verifiedTdaPassenger?.lastName ?? "",
    contactEmail: "",
    mobileNoCountryCode: "+60",
    contactPhone: "",
  }));

  const isFormValid = contactSchema.safeParse(values).success;

  const [dialCodes, setDialCodes] = useState<DialCode[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const setContact = useContactStore((state) => state.setContact);

  // Expose the fixed action bar height so the layout's bottom-hero sits above it
  const footerRef = useActionBarHeight<HTMLElement>();

  const validateField = (key: keyof ContactValues, value: string) => {
    const result = contactSchema.shape[key].safeParse(value);

    return result.success ? "" : (result.error.issues[0]?.message ?? "");
  };

  useEffect(() => {
    const fetchDialCodes = async () => {
      try {
        const response = await GetDialCode();

        setDialCodes(response.data?.dialCodeList ?? []);
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to get dial codes.",
        );
      }
    };

    fetchDialCodes();
  }, []);

  const handleNext = async () => {
    if (loading) return;

    if (!paymentId) {
      toast.error("Payment ID is missing.");
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const agentCompanySellerId = useAuthStore.getState().user?.agentCompanySellerId ?? "";

      const saveContactResponse = await SaveContact({
        agentCompanySellerId,
        paymentId,
        firstName: values.firstName,
        lastName: values.lastName,
        contactEmail: values.contactEmail,
        mobileNoCountryCode: values.mobileNoCountryCode,
        mobileNo: values.contactPhone,
      });

      if (!saveContactResponse.status) {
        toast.error(saveContactResponse.message ?? "Failed to save contact.");
        return;
      }

      setContact(values);

      const invoiceResponse = await GetInvoice({
        agentCompanySellerId,
        paymentId,
      });

      navigate(`/purchase-ticket/booking-summary/${paymentId}`, {
        state: {
          invoice: invoiceResponse.data,
        },
      });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to get invoice.",
      );
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    {
      key: "firstName" as const,
      label: "First name",
      placeholder: "Enter first name",
      type: "text",
    },
    {
      key: "lastName" as const,
      label: "Last name",
      placeholder: "Enter last name",
      type: "text",
    },
    {
      key: "contactEmail" as const,
      label: "Email address",
      placeholder: "Enter contact email",
      type: "email",
    },
  ];

  const formatContactName = (value: string) => {
    return value
      .replace(/[^a-zA-Z\s]/g, "")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <div className="max-w-7xl mx-auto px-4 pt-4 pb-16 sm:px-6 lg:px-8">
      <h1 className="text-secondary-foreground text-lg font-semibold">
        Contact Info
      </h1>

      <section className="mt-2 md:mt-4  rounded-2xl bg-card px-4 py-6 md:p-6 shadow-xl">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {fields.map((field) => (
            <div key={field.key} className="flex flex-col gap-2">
              <Label
                className="text-xs font-bold text-field"
                htmlFor={field.key}
              >
                {field.label}
                <span className="text-destructive">*</span>
              </Label>

              <Input
                id={field.key}
                type={field.type}
                className={cn(
                  FIELD_CLASS,
                  touched[field.key] &&
                    errors[field.key] &&
                    "border-red-400 focus-visible:border-red-400",
                )}
                placeholder={field.placeholder}
                value={values[field.key]}
                maxLength={50}
                onChange={(e) => {
                  const value = e.target.value;

                  if (field.key === "firstName" || field.key === "lastName") {
                    const formattedValue = formatContactName(value);

                    setValues((current) => ({
                      ...current,
                      [field.key]: formattedValue,
                    }));

                    // Only validate after user has touched the field
                    if (touched[field.key]) {
                      const error = validateField(field.key, formattedValue);

                      setErrors((current) => ({
                        ...current,
                        [field.key]: error,
                      }));
                    }
                  } else {
                    const emailValue = value.slice(0, 50);

                    setValues((current) => ({
                      ...current,
                      contactEmail: emailValue,
                    }));

                    // Only validate after user has touched the field
                    if (touched.contactEmail) {
                      const error = validateField("contactEmail", emailValue);

                      setErrors((current) => ({
                        ...current,
                        contactEmail: error,
                      }));
                    }
                  }
                }}
                onBlur={() => {
                  setTouched((current) => ({
                    ...current,
                    [field.key]: true,
                  }));

                  const error = validateField(field.key, values[field.key]);

                  setErrors((current) => ({
                    ...current,
                    [field.key]: error,
                  }));
                }}
              />

              {errors[field.key] ? (
                <p className="text-xs text-destructive">{errors[field.key]}</p>
              ) : null}
            </div>
          ))}

          {/* Phone */}
          <div className="md:col-span-1">
            <PhoneField
              code={values.mobileNoCountryCode}
              phone={values.contactPhone}
              dialCodes={dialCodes}
              error={errors.contactPhone}
              onCodeChange={(value) => {
                setValues((current) => ({
                  ...current,
                  mobileNoCountryCode: value,
                }));

                if (touched.contactPhone) {
                  const result = contactSchema.safeParse({
                    ...values,
                    mobileNoCountryCode: value,
                  });

                  const error = result.success
                    ? ""
                    : (result.error.issues.find(
                        (issue) => issue.path[0] === "contactPhone",
                      )?.message ?? "");

                  setErrors((current) => ({
                    ...current,
                    contactPhone: error,
                  }));
                }
              }}
              onPhoneChange={(value) => {
                let phone = value.replace(/\D/g, "");

                if (values.mobileNoCountryCode === "+60") {
                  // Remove leading 0
                  phone = phone.replace(/^0+/, "");
                }

                // Max input remains 15 digits
                phone = phone.slice(0, 15);

                const newValues = {
                  ...values,
                  contactPhone: phone,
                };

                setValues(newValues);

                // Validate immediately while typing
                const result = contactSchema.safeParse(newValues);

                const error = result.success
                  ? ""
                  : (result.error.issues.find(
                      (issue) => issue.path[0] === "contactPhone",
                    )?.message ?? "");

                setErrors((current) => ({
                  ...current,
                  contactPhone: error,
                }));
              }}
              onPhoneBlur={() => {
                setTouched((current) => ({
                  ...current,
                  contactPhone: true,
                }));

                const result = contactSchema.safeParse(values);

                const error = result.success
                  ? ""
                  : (result.error.issues.find(
                      (issue) => issue.path[0] === "contactPhone",
                    )?.message ?? "");

                setErrors((current) => ({
                  ...current,
                  contactPhone: error,
                }));
              }}
            />
          </div>
        </div>

        <div className="hidden justify-end md:col-span-2 md:flex">
          <Button
            type="button"
            variant="secondary"
            className="w-2xs cursor-pointer"
            onClick={handleNext}
            disabled={!isFormValid || loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Next"
            )}
          </Button>
        </div>
      </section>

      <footer
        ref={footerRef}
        className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card md:hidden"
      >
        <div className="mx-auto flex w-full max-w-7xl items-center gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Button
            type="button"
            variant="secondary"
            className="w-full cursor-pointer"
            onClick={handleNext}
            disabled={!isFormValid || loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Next"
            )}
          </Button>
        </div>
      </footer>
    </div>
  );
}
